const DOM_DELTA_LINE = 1;
const DOM_DELTA_PAGE = 2;
const MIN_PLOT_WIDTH_PX = 80;
const LINE_HEIGHT_PX = 16;
const ZOOM_GAIN = 0.25;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export function normalizeGexWheelDelta(deltaY, deltaMode, plotWidth) {
  if (!Number.isFinite(deltaY)) return 0;
  if (deltaMode === DOM_DELTA_LINE) return deltaY * LINE_HEIGHT_PX;
  if (deltaMode === DOM_DELTA_PAGE) return deltaY * Math.max(MIN_PLOT_WIDTH_PX, plotWidth);
  return deltaY;
}

export function clampGexTimeRange(range, domain, minSpan, anchorFraction = 1) {
  const domainFrom = Number(domain?.from);
  const domainTo = Number(domain?.to);
  const rangeFrom = Number(range?.from);
  const rangeTo = Number(range?.to);
  if (![domainFrom, domainTo, rangeFrom, rangeTo].every(Number.isFinite)
    || domainTo <= domainFrom
    || rangeTo <= rangeFrom) {
    return null;
  }

  const domainSpan = domainTo - domainFrom;
  const span = clamp(Math.max(rangeTo - rangeFrom, minSpan), 1, domainSpan);
  if (span >= domainSpan) return { from: domainFrom, to: domainTo };

  const fraction = clamp(anchorFraction, 0, 1);
  const anchor = rangeFrom + fraction * (rangeTo - rangeFrom);
  let from = anchor - fraction * span;
  let to = from + span;
  if (from < domainFrom) {
    to += domainFrom - from;
    from = domainFrom;
  }
  if (to > domainTo) {
    from -= to - domainTo;
    to = domainTo;
  }
  return { from: Math.max(domainFrom, from), to: Math.min(domainTo, to) };
}

const QUARTER_HOUR_MS = 15 * 60 * 1000;
const MAX_AXIS_TICKS = 512;

/**
 * Tick times for a GEX time axis, taken from the window rather than the series.
 *
 * Index-derived ticks vanish whenever the window holds no point, which is
 * reachable because the wheel clamps to the session close rather than to the
 * last bar. Projecting from the range is what lets the Flowmap keep labelling
 * the reserved future, and the GEX x-scale is linear in time, so the window
 * edges plus the aligned boundaries between them describe the whole axis.
 */
export function gexTimeAxisTicks(from, to, stepMs = QUARTER_HOUR_MS) {
  // `Number(null)` is 0, which is finite: without this the epoch would become
  // the window start and the loop below would walk 56 years of quarter-hours.
  if (from == null || to == null) return [];
  const start = Number(from);
  const end = Number(to);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];

  const step = Math.max(1, Number(stepMs) || QUARTER_HOUR_MS);
  // A trading day holds 26 quarter-hours. Anything past the cap is malformed
  // input rather than a view, and stepping across it is unbounded work.
  if ((end - start) / step > MAX_AXIS_TICKS) return [start, end];

  const ticks = [start];
  for (let tick = Math.ceil(start / step) * step; tick < end; tick += step) {
    if (tick > start) ticks.push(tick);
  }
  ticks.push(end);
  return ticks;
}

export function zoomGexTimeRange({
  range,
  domain,
  deltaY,
  deltaMode = 0,
  plotWidth,
  anchorFraction,
  minSpan,
}) {
  const currentSpan = Number(range?.to) - Number(range?.from);
  const domainSpan = Number(domain?.to) - Number(domain?.from);
  if (!(currentSpan > 0) || !(domainSpan > 0)) return null;

  const width = Math.max(MIN_PLOT_WIDTH_PX, Number(plotWidth) || 0);
  const normalizedDelta = normalizeGexWheelDelta(deltaY, deltaMode, width);
  const ratio = clamp(normalizedDelta / width, -1, 1);
  const factor = Math.exp(ratio * ZOOM_GAIN);
  const nextSpan = clamp(currentSpan * factor, Math.max(1, minSpan), domainSpan);
  const fraction = clamp(anchorFraction, 0, 1);
  const anchor = Number(range.from) + fraction * currentSpan;
  const candidate = {
    from: anchor - fraction * nextSpan,
    to: anchor + (1 - fraction) * nextSpan,
  };
  return clampGexTimeRange(candidate, domain, nextSpan, fraction);
}
